import React from "react";
import type { EffectLifetime, TileEffectState } from "../types/cardTypes";
import { useNowTicker } from "../hooks/useNowTicker";
import { expiryReadout, formatRemaining, formatWallClockEnd, msUntil } from "../lib/expiry";
import "../styles/cards.css";

/**
 * One pill, several modes. The mode is chosen by how the card is being
 * shown:
 *
 *  - `hand`      — an unplayed in_hand copy: "Expires from hand in 3d 4h",
 *                  amber < 24h / red < 6h.
 *  - `effect`    — a live played effect: reads its real end from the
 *                  ActiveEffect join (`TileEffectState`) — wall-clock end for
 *                  fixed/anchored windows, "N/total" for charge banks,
 *                  pending/live/over for an anchored window, the lifetime
 *                  label for until-event / end-of-day.
 *
 * 30s ticks via useNowTicker keep the countdown honest without each pill
 * owning a timer.
 */
export type CardExpiryPillProps =
  | {
      mode: "hand";
      /** ISO hand-expiry (soonest across in_hand copies). Null → renders nothing. */
      expiresAt: string | null;
      className?: string;
    }
  | {
      mode: "effect";
      effect: TileEffectState;
      lifetime?: EffectLifetime | null;
      className?: string;
    };

function HandPill({ expiresAt, className }: { expiresAt: string | null; className?: string }) {
  const now = useNowTicker(30_000);
  const readout = expiryReadout(expiresAt, now);
  if (!readout) return null;
  if (readout.expired) {
    return (
      <span className={pillClass("urgent", className)}>Expired from hand</span>
    );
  }
  return (
    <span className={pillClass(readout.urgency, className)}>
      Expires from hand in {readout.label}
    </span>
  );
}

function EffectPill({
  effect,
  lifetime,
  className,
}: {
  effect: TileEffectState;
  lifetime?: EffectLifetime | null;
  className?: string;
}) {
  const now = useNowTicker(30_000);
  const kind = lifetime?.kind;

  // Charge banks — remaining/total, no wall clock.
  if (effect.charges) {
    return (
      <span className={pillClass("calm", className)}>
        {effect.charges.remaining}/{effect.charges.total} charges
      </span>
    );
  }

  // Anchored windows (Sprint Lead / Reverse / Gridlock): pending → live → over.
  if (kind === "anchored_window") {
    if (effect.state === "pending") {
      return <span className={pillClass("calm", className)}>Armed — waiting for the race</span>;
    }
    const delta = msUntil(effect.expiresAt, now);
    if (delta !== null && delta <= 0) {
      return <span className={pillClass("calm", className)}>Window over</span>;
    }
    if (delta !== null) {
      return (
        <span className={pillClass("calm", className)}>Live · {formatRemaining(delta)} left</span>
      );
    }
    return <span className={pillClass("calm", className)}>{lifetime?.label ?? "Armed"}</span>;
  }

  // Fixed windows — a concrete wall-clock end plus the remaining time.
  if (kind === "fixed" && effect.expiresAt) {
    const delta = msUntil(effect.expiresAt, now);
    const end = formatWallClockEnd(effect.expiresAt);
    if (delta !== null && delta > 0 && end) {
      return (
        <span className={pillClass("calm", className)}>
          Active · ends {end} ({formatRemaining(delta)})
        </span>
      );
    }
  }

  // Until-event / end-of-day: no honest countdown — render the lifetime label
  // (e.g. "Until the first Heist", "Until the end of today").
  if (kind === "until_event" || kind === "end_of_day") {
    return <span className={pillClass("calm", className)}>{lifetime?.label ?? "Active"}</span>;
  }

  // Fallback: any remaining wall clock, otherwise the label, otherwise "Active".
  const delta = msUntil(effect.expiresAt, now);
  if (delta !== null && delta > 0) {
    return <span className={pillClass("calm", className)}>Active · {formatRemaining(delta)} left</span>;
  }
  return <span className={pillClass("calm", className)}>{effect.label || "Active"}</span>;
}

function pillClass(urgency: "calm" | "warn" | "urgent", className?: string): string {
  return ["card-expiry-pill", `card-expiry-pill--${urgency}`, className || ""]
    .filter(Boolean)
    .join(" ");
}

export default function CardExpiryPill(props: CardExpiryPillProps) {
  if (props.mode === "hand") {
    return <HandPill expiresAt={props.expiresAt} className={props.className} />;
  }
  return (
    <EffectPill effect={props.effect} lifetime={props.lifetime} className={props.className} />
  );
}
